import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { clsx } from 'clsx';
import type { CreativeSiblingSummary } from './types';

interface Props {
  siblings: CreativeSiblingSummary[];
  currentCreativeId: string;
}

const STATUS_DOT: Record<string, string> = {
  active: 'bg-emerald-400',
  paused: 'bg-amber-400',
  draft: 'bg-slate-300',
  completed: 'bg-blue-400',
  archived: 'bg-slate-200',
  enriched: 'bg-emerald-400',
  extracted: 'bg-blue-400',
  fetched: 'bg-slate-300',
  discovered: 'bg-slate-200',
};

export function SiblingCreativeStrip({ siblings, currentCreativeId }: Props) {
  const navigate = useNavigate();

  if (siblings.length <= 1) return null;

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Siblings ({siblings.length})
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {siblings.map((sibling) => {
          const isCurrent = sibling.creative_id === currentCreativeId || sibling.is_current;
          return (
            <button
              key={sibling.creative_id}
              onClick={() => {
                if (!isCurrent) {
                  navigate(
                    `/creative/assets/creative/${encodeURIComponent(sibling.creative_id)}`,
                  );
                }
              }}
              title={sibling.headline || sibling.creative_id}
              className={clsx(
                'relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all',
                isCurrent
                  ? 'border-teal-500 ring-2 ring-teal-200 cursor-default'
                  : 'border-slate-200 hover:border-teal-300 cursor-pointer',
              )}
            >
              {sibling.thumbnail_url && /^https?:/i.test(sibling.thumbnail_url) ? (
                <img
                  src={sibling.thumbnail_url}
                  alt={sibling.headline}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                  <Star size={16} className="text-slate-300" />
                </div>
              )}

              <span
                className={clsx(
                  'absolute top-1 left-1 w-2 h-2 rounded-full border border-white shadow-sm',
                  STATUS_DOT[sibling.status] ?? STATUS_DOT.draft,
                )}
              />

              {(sibling.review_score != null || sibling.lifetime_roas != null) && (
                <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1 py-0.5">
                  <span className="text-[9px] font-mono text-white">
                    {sibling.review_score != null
                      ? `${sibling.review_score.toFixed(1)}/10`
                      : `${sibling.lifetime_roas!.toFixed(1)}×`}
                  </span>
                </div>
              )}

              {isCurrent && (
                <div className="absolute top-1 right-1">
                  <span className="text-[8px] font-mono bg-teal-500 text-white rounded px-1">
                    NOW
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
